/**
 * Hook: Priority-Support Benachrichtigungs-Einstellungen
 *
 * Lädt die Settings beim Mount, bietet `update`-Funktion mit Optimistic UI.
 * Wird im Profil-Tab "Benachrichtigungen" konsumiert.
 *
 * Optimistic Pattern:
 *   - User klickt Toggle → setState sofort umschalten
 *   - REST-PUT im Hintergrund
 *   - Bei Fehler: zurückrollen + Error setzen
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
  type NotificationPreferencesPatch
} from '../services/support/notificationPreferencesService';

interface UseNotificationPreferencesOptions {
  enabled?: boolean;
}

interface UseNotificationPreferencesResult {
  preferences: NotificationPreferences | null;
  isLoading: boolean;
  error: Error | null;
  /** Optimistisch updaten + REST-PUT senden. Roll-back bei Fehler. */
  update: (patch: NotificationPreferencesPatch) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useNotificationPreferences(
  options: UseNotificationPreferencesOptions = {}
): UseNotificationPreferencesResult {
  const enabled = options.enabled !== false;
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const prefs = await fetchNotificationPreferences();
      setPreferences(prefs);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Einstellungen konnten nicht geladen werden'));
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setPreferences(null);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const update = useCallback(async (patch: NotificationPreferencesPatch): Promise<void> => {
    setError(null);
    const previous = preferences;

    // Optimistic — sofort umschalten
    if (previous) {
      setPreferences({ ...previous, ...patch });
    }

    try {
      const next = await updateNotificationPreferences(patch);
      setPreferences(next);
    } catch (err) {
      // Roll-back
      if (previous) setPreferences(previous);
      setError(err instanceof Error ? err : new Error('Einstellungen konnten nicht gespeichert werden'));
      throw err;
    }
  }, [preferences]);

  return { preferences, isLoading, error, update, refresh };
}
